/** @domain content -- Generic text fields: name, description, remarks, title, comment, ticker, message. */
import { z } from 'zod';
import { createDomainField } from './types';
import { trimExtraSpaces, uppercaseOnly } from './formatters';

const define = createDomainField('content');

// Shared label 'Name' with personal.personName — this is for content/template names; personal.personName is for person names
export const name = define({
  label: 'Name',
  placeholder: 'Enter name',
  schema: z
    .string()
    .max(100, 'Must be at most 100 characters')
    .regex(/^\s*\S[\s\S]*$/, 'Must not be blank')
    .refine((v) => !v.includes('  '), 'Must not contain double spaces'),
  adapterHint: 'text',
  formatter: trimExtraSpaces,
  displayFormat: 'text',
});

export const textarea = define({
  label: 'Text',
  schema: z.string().max(1000, 'Must be at most 1000 characters'),
  adapterHint: 'textarea',
  displayFormat: 'text',
});

export const textareaLong = define({
  label: 'Text',
  schema: z.string().max(2000, 'Must be at most 2000 characters'),
  adapterHint: 'textarea',
  displayFormat: 'text',
});

export const description = define({
  ...textarea,
  label: 'Description',
  placeholder: 'Enter description',
});

export const remarks = define({
  ...textarea,
  label: 'Remarks',
  placeholder: 'Enter remarks',
});

export const ticker = define({
  label: 'Ticker Symbol',
  placeholder: 'e.g. AAPL',
  schema: z.string().regex(/^[A-Z]{1,10}([-.][A-Z]{1,3})?$/, 'Must be a valid ticker symbol'),
  adapterHint: 'text',
  formatter: uppercaseOnly,
  displayFormat: 'text',
});

export const agreementStatement = define({
  ...textareaLong,
  label: 'Agreement Statement',
  placeholder: 'Enter agreement statement',
  schema: z.string().max(5000, 'Must be at most 5000 characters'),
});

export const summary = define({
  ...textareaLong,
  label: 'Summary',
  placeholder: 'Enter summary',
});

export const message = define({
  label: 'Message',
  placeholder: 'Enter message',
  schema: z.string(),
  adapterHint: 'richtext',
  displayFormat: 'html',
});

export const title = define({
  label: 'Title',
  placeholder: 'Enter title',
  schema: z
    .string()
    .max(200, 'Must be at most 200 characters')
    .regex(/^\s*\S[\s\S]*$/, 'Must not be blank'),
  adapterHint: 'text',
  formatter: trimExtraSpaces,
  displayFormat: 'text',
});

export const comment = define({
  ...textareaLong,
  label: 'Comment',
  placeholder: 'Enter comment',
});

/** Generic read-only text display — use overrideLabel for context-specific labels in views. */
export const plainText = define({
  label: 'Text',
  schema: z.string(),
  adapterHint: 'text',
  displayFormat: 'text',
});

export const accountName = define({ ...plainText, label: 'Account Name' });
export const accountNumber = define({ ...plainText, label: 'Account Number' });
export const accountStatus = define({ ...plainText, label: 'Account Status' });
